import { useEffect, useMemo } from 'react';
import HeaderSEO from 'components/HeaderSEO';
import { Container } from 'react-bootstrap';

import appConfig, { routePaths } from 'appConfig';
import {
  Page,
  ManualBreadcrumb,
  ManualBreadcrumbItem,
} from 'components/common';
import { getProductPath } from 'lib/router';
import {
  api,
  useProduct,
  findFirstCoverImageUrl,
  makeProductCacheKey,
} from 'lib/product';
import {
  MainInformation,
  ProductCustomizationContextProvider,
  ProductStickyBar,
  ProductsSection,
} from 'lib/sellingProduct';
import { ProductStructuredData } from 'lib/productSEO';
import * as analytics from 'lib/analytics';
import InitialProductVariantSetterSuspense from 'lib/sellingProduct/InitialProductVariantSetterSuspense';
import { useRouter } from 'next/router';
import { extractAddonsFromQuery } from 'lib/sellingProduct/ProductCustomizationContext';
import { usePopupContext } from 'lib/popups';
import { getSession } from 'next-auth/client';
import { useAuth } from 'lib/auth';

const ProductPage = ({
  initialProduct,
  catalogueIds,
  categories,
  collections,
  specialProductCategories,
}) => {
  const { id } = initialProduct;
  const router = useRouter();
  const {
    trackProductView,
    triggerPostAddToCartPopup,
  } = usePopupContext();

  const { loading } = useAuth();

  const { product } = useProduct(
    id,
    {
      fallback: {
        [makeProductCacheKey(id)]: initialProduct,
      },
    },
    !loading,
  );

  useEffect(() => {
    if (product) {
      analytics.viewProduct({ product });
      trackProductView(product.id);
    }
  }, [product, trackProductView]);

  const initialSelectedAddons = useMemo(
    () => extractAddonsFromQuery(router.query),
    [router.query],
  );

  return (
    <Page
      breadcrumb={
        <ManualBreadcrumb>
          <ManualBreadcrumbItem href={routePaths.HOME}>
            Home
          </ManualBreadcrumbItem>
          <ManualBreadcrumbItem href={routePaths.PRODUCTS}>
            Products
          </ManualBreadcrumbItem>
          <ManualBreadcrumbItem active>
            {product.name}
          </ManualBreadcrumbItem>
        </ManualBreadcrumb>
      }
      content={
        <ProductCustomizationContextProvider
          initialSelectedAddons={initialSelectedAddons}
        >
          <HeaderSEO
            title={product.name}
            description={product.description}
            path={`https://www.yummybros.com/product/${product.slug}`}
            image={findFirstCoverImageUrl(product)}
            ogType="article"
          />
          <ProductStructuredData product={product} />

          <InitialProductVariantSetterSuspense
            product={product}
            fallback="Loading..."
          >
            <Container>
              <MainInformation
                product={product}
                catalogueIds={catalogueIds}
                onFirstItemAdded={triggerPostAddToCartPopup}
                categories={categories}
                collections={collections}
                specialProductCategories={specialProductCategories}
              />

              {appConfig.featureToggle.newReleasedProductsSection ===
                'true' && (
                <ProductsSection
                  title="New Releases"
                  query={{
                    sort_dir: 'desc',
                    sort_field: 'published_at',
                  }}
                />
              )}
            </Container>

            <ProductStickyBar
              product={product}
              onFirstItemAdded={triggerPostAddToCartPopup}
            />
          </InitialProductVariantSetterSuspense>
        </ProductCustomizationContextProvider>
      }
    />
  );
};

ProductPage.propTypes = {};

export const getServerSideProps = async (ctx) => {
  const { slugOrProductId } = ctx.query || {};
  const session = await getSession(ctx);
  const accessToken = session?.user.accessToken;

  const authHeaders = accessToken
    ? { Authorization: `Bearer ${accessToken}` }
    : undefined;

  try {
    const { error, response } = await api.getProductBySlug(
      slugOrProductId,
      { rawError: true },
      authHeaders,
    );

    const catalogueIds =
      process.env.CATALOGUES_MEGA_MENU_BY_KCAL_RANGE_IDS ?? '';

    const newLaunchesCollections =
      process.env.NEW_LAUNCHES_COLLECTION_ID;
    const hotSellerCollections =
      process.env.HOT_SELLERS_COLLECTION_ID;

    let collections = [];
    if (newLaunchesCollections)
      collections.push(JSON.parse(newLaunchesCollections));

    if (hotSellerCollections)
      collections.push(JSON.parse(hotSellerCollections));

    const categories = process.env.CATEGORIES
      ? JSON.parse(process.env.CATEGORIES)
      : [];

    const specialProductCategories = process.env
      .SPECIAL_PRODUCT_CATEGORIES
      ? JSON.parse(process.env.SPECIAL_PRODUCT_CATEGORIES)
      : [];

    if (error) throw error;

    return {
      props: {
        query: { slugOrProductId },
        initialProduct: response.data,
        catalogueIds,
        categories,
        collections,
        specialProductCategories,
      },
    };
  } catch (err) {
    if (err.response?.status === 409) {
      const specialProductCategories = process.env
        .SPECIAL_PRODUCT_CATEGORIES
        ? JSON.parse(process.env.SPECIAL_PRODUCT_CATEGORIES)
        : [];

      const specialProduct = specialProductCategories[0];
      const categoryIds =
        specialProduct?.id?.split(',').filter(Boolean) ?? [];
      const params = new URLSearchParams();

      categoryIds.forEach((id) => {
        params.append('category_ids', id);
      });

      return {
        redirect: {
          destination: `${routePaths.PRODUCTS}?${params.toString()}`,
          permanent: false,
        },
      };
    }

    // if cannot query by slug, maybe the URL param is an ID so try query by ID
    try {
      const { error, response } = await api.getProductById(
        slugOrProductId,
      );

      if (error) throw error;

      const product = response.data;

      // prefer slug URL, redirect
      return {
        redirect: {
          destination: getProductPath(product.slug),
          permanent: false,
        },
      };
    } catch {
      return { notFound: true };
    }
  }
};

export default ProductPage;
